import { IconName } from "@pathwright/pathicons"
import { ReactNode, createContext } from "react"
import { match as RouteMatch, generatePath } from "react-router-dom"
import { CoverPageOverviewImmersive } from "../CoverPageDescription"
import CoverPageMore from "../CoverPageMore"
import { CoverPageOfferImmersive } from "../CoverPageOffer"
import CoverPageContributorImmersive from "../contributors/CoverPageContributorImmersive"
import { CoverPagePreviewImmersive } from "../path-preview/CoverPagePreviewImmersive"
import { CoverPageOfferSandboxImmersive } from "../tests/CoverPageOfferSandbox"
import { CoverPageTrailerImmersive } from "../trailer/CoverPageTrailer"
import { CoverPageContextType, ImmersiveRoute } from "../types"

// Immersive views of a Cover Page, each mounted as a React Router child route
// of the cover page route. Also drives the immersive navigation UI.

export type ImmersiveRouteConfig = {
  key: ImmersiveRoute["name"]
  // React Router path, relative to the cover page route
  path: string
  // Path param holding the ImmersiveRoute id, for routes with one
  idParam?: string
  // Get immersive routes for routes that are actually active (based on context data)
  getNavigableImmersiveRoutes: (
    coverPageContext: CoverPageContextType
  ) => ImmersiveRoute[]
  // Render function for the immersive content
  render: (args?: any) => JSX.Element
  // Should this route be rendered at all?
  shouldRender?: (coverPageContext: CoverPageContextType) => boolean
  // If displayAsNavLabel is true, this label will be displayed in the navigation
  navLabel?: string | ReactNode
  // Icon to display in the navigation
  navIcon?: IconName
  // Whether to display a link to this route in the navigation
  displayAsNavLabel?:
    | boolean
    | ((coverPageContext: CoverPageContextType) => boolean)
  // Groups routes that should highlight the same nav item
  navGroup?: string
  // Maximum width of the immersive content for this route
  maxWidth?: string | number
}

export const immersiveRoutes: ImmersiveRouteConfig[] = [
  {
    key: "overview",
    path: "overview/",
    navIcon: "list-one-column-fill",
    displayAsNavLabel: ({ resource }) => !!resource?.description,
    render: () => <CoverPageOverviewImmersive />,
    shouldRender: ({ resource }) => !!resource?.description,
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource?.description ? [{ name: "overview" }] : [],
    maxWidth: "650px"
  },
  {
    key: "trailer",
    path: "trailer/",
    navIcon: "video",
    displayAsNavLabel: ({ resource }) => !!resource?.trailer_embed_code,
    render: () => <CoverPageTrailerImmersive />,
    shouldRender: ({ resource }) => !!resource?.trailer_embed_code,
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource?.trailer_embed_code ? [{ name: "trailer" }] : [],
    maxWidth: "900px"
  },
  {
    key: "contributor",
    path: "contributor/:contributorId(\\d+)/",
    idParam: "contributorId",
    navGroup: "contributors",
    navIcon: "avatar-circle-fill",
    displayAsNavLabel: ({ contributors }) => contributors?.length > 0,
    shouldRender: ({ contributors }) => contributors?.length > 0,
    render: ({ id }: { id: number }) => (
      <CoverPageContributorImmersive id={id} />
    ),
    getNavigableImmersiveRoutes: ({ contributors }) =>
      contributors?.length > 0
        ? contributors.map((c) => ({
            name: "contributor",
            id: c.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  {
    key: "preview",
    path: "preview/:previewId(path|\\d+)/",
    idParam: "previewId",
    navIcon: "eye",
    navGroup: "previews",
    displayAsNavLabel: ({ previewNavItems }) => previewNavItems?.length > 0,
    shouldRender: ({ previewNavItems }) => previewNavItems?.length > 0,
    render: ({ id }: { id: number | "path" }) => (
      <CoverPagePreviewImmersive id={id} />
    ),
    getNavigableImmersiveRoutes: ({ previewNavItems }) =>
      previewNavItems.map((item) => ({
        name: "preview",
        id: item.source_id
      })),
    maxWidth: "800px"
  },
  {
    key: "offers",
    path: "offers/",
    render: () => <CoverPageOfferSandboxImmersive />,
    displayAsNavLabel: false,
    // Offers sandbox route is intentionally non-navigable in the standard immersive nav.
    getNavigableImmersiveRoutes: () => [],
    maxWidth: "1200px"
  },
  {
    key: "offer",
    path: "offer/:offerId(\\d+)/",
    idParam: "offerId",
    navIcon: "chevron-right",
    navGroup: "offers",
    displayAsNavLabel: ({ offers }) => offers?.length > 0,
    shouldRender: ({ offers }) => offers?.length > 0,
    render: ({ id }: { id: number }) => <CoverPageOfferImmersive id={id} />,
    getNavigableImmersiveRoutes: ({ offers }) =>
      offers?.length > 0
        ? offers.map((o) => ({
            name: "offer",
            id: o.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  {
    key: "more",
    path: "more/",
    navIcon: "ellipses-o",
    displayAsNavLabel: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0,
    shouldRender: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0,
    render: () => <CoverPageMore />,
    getNavigableImmersiveRoutes: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0
        ? [{ name: "more" }]
        : [],
    maxWidth: "900px"
  }
]

export const getImmersiveRouteConfig = (name?: ImmersiveRoute["name"]) =>
  immersiveRoutes.find((r) => r.key === name)

const toImmersiveRouteId = (value: string) =>
  /^\d+$/.test(value) ? Number(value) : value

// Join a cover page route (pattern or URL) with a relative immersive path.
export const joinImmersivePath = (coverPath: string, path: string) =>
  `${coverPath.replace(/\/+$/, "")}/${path}`

/** Route paths for the cover page at `coverPath` and each of its immersive views. */
export const getCoverPagePaths = (coverPath: string) => [
  coverPath,
  ...immersiveRoutes.map((config) => joinImmersivePath(coverPath, config.path))
]

/**
 * The immersive route a React Router match is for, where `coverPath` is the
 * cover page route pattern. Null on the cover page itself.
 */
export function getImmersiveRouteForMatch(
  coverPath: string,
  match: Pick<
    RouteMatch<Record<string, string | undefined>>,
    "path" | "params"
  > | null
): ImmersiveRoute | null {
  if (!match) return null
  const config = immersiveRoutes.find(
    (config) => joinImmersivePath(coverPath, config.path) === match.path
  )
  if (!config) return null
  if (!config.idParam) return { name: config.key }
  const id = match.params[config.idParam]
  return id == null ? null : { name: config.key, id: toImmersiveRouteId(id) }
}

// The cover page route pattern that immersive child routes are relative to.
export const CoverPagePathContext = createContext<string | null>(null)

/**
 * URL for an immersive route under `coverUrl` (this resource's cover page URL).
 * Null when the route is unknown or its id doesn't satisfy the route's path.
 */
export function buildImmersivePath(
  coverUrl: string,
  route: ImmersiveRoute
): string | null {
  const config = getImmersiveRouteConfig(route.name)
  if (!config) return null
  if (config.idParam && route.id == null) return null

  try {
    const path = generatePath(
      config.path,
      config.idParam ? { [config.idParam]: String(route.id) } : {}
    )
    return joinImmersivePath(coverUrl, path)
  } catch {
    return null
  }
}
